import type { OptionChainContract } from '../api/types'
import { Table } from './ui/Table'

export interface ChainRow {
  strike: number
  strikeText: string
  call?: OptionChainContract
  put?: OptionChainContract
}

interface Props {
  rows: ChainRow[]
  atmStrike: number | null
  armedKey: string | null
  submittingKey: string | null
  onCellClick: (
    side: 'CALL' | 'PUT',
    action: 'BUY' | 'SELL',
    contract: OptionChainContract,
  ) => void
  /** When non-null, CALL BUY (ask) cells render disabled with this as the tooltip. */
  callBuyDisabledReason?: string | null
  /** When non-null, PUT BUY (ask) cells render disabled with this as the tooltip. */
  putBuyDisabledReason?: string | null
}

function fmtPrice(v: string | undefined): string {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return v
  return n.toFixed(2)
}

function fmtInt(v: number | undefined): string {
  if (v == null) return '—'
  return v.toLocaleString('en-US')
}

function cellKey(symbol: string, action: 'BUY' | 'SELL'): string {
  return `${symbol}:${action}`
}

interface PriceCellProps {
  contract: OptionChainContract | undefined
  field: 'bid' | 'ask'
  side: 'CALL' | 'PUT'
  armedKey: string | null
  submittingKey: string | null
  onClick: (action: 'BUY' | 'SELL', c: OptionChainContract) => void
  buyDisabledReason: string | null
}

function PriceCell({
  contract,
  field,
  side,
  armedKey,
  submittingKey,
  onClick,
  buyDisabledReason,
}: PriceCellProps) {
  if (!contract) return <td className="px-2 py-1.5 text-right font-mono text-zinc-600">—</td>
  const action: 'BUY' | 'SELL' = field === 'ask' ? 'BUY' : 'SELL'
  const key = cellKey(contract.instrument.symbol, action)
  const isArmed = armedKey === key
  const isSubmitting = submittingKey === key
  const anySubmitting = submittingKey !== null
  const buyBlocked = action === 'BUY' && buyDisabledReason != null
  const baseColor =
    action === 'BUY'
      ? 'text-emerald-400 hover:bg-emerald-900/40'
      : 'text-red-400 hover:bg-red-900/40'
  const armedStyle =
    action === 'BUY'
      ? 'bg-emerald-600 text-white ring-1 ring-emerald-300 animate-pulse'
      : 'bg-red-600 text-white ring-1 ring-red-300 animate-pulse'
  return (
    <td className="px-1 py-0.5 text-right">
      <button
        type="button"
        disabled={anySubmitting || buyBlocked}
        onClick={() => onClick(action, contract)}
        title={buyBlocked ? (buyDisabledReason ?? undefined) : `${action} ${side} @ ${field} ${fmtPrice(contract[field])}`}
        className={`w-full px-1.5 py-1 rounded font-mono text-right transition disabled:opacity-50 disabled:cursor-not-allowed ${
          isArmed ? armedStyle : baseColor
        }`}
      >
        {isSubmitting ? '…' : fmtPrice(contract[field])}
      </button>
    </td>
  )
}

export function OptionsChainTable({
  rows,
  atmStrike,
  armedKey,
  submittingKey,
  onCellClick,
  callBuyDisabledReason = null,
  putBuyDisabledReason = null,
}: Props) {
  if (rows.length === 0) {
    return <div className="p-6 text-center text-zinc-500 text-sm">No strikes available.</div>
  }

  return (
    <Table dense>
      <thead>
          <tr className="text-zinc-500 uppercase">
            <th colSpan={5} className="px-2 py-2 text-center border-b border-zinc-800 bg-emerald-950/20">
              Calls
            </th>
            <th className="px-2 py-2 text-center border-b border-zinc-800">Strike</th>
            <th colSpan={5} className="px-2 py-2 text-center border-b border-zinc-800 bg-red-950/20">
              Puts
            </th>
          </tr>
          <tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-800">
            <th className="px-2 py-1 text-right">OI</th>
            <th className="px-2 py-1 text-right">Vol</th>
            <th className="px-2 py-1 text-right">Last</th>
            <th className="px-2 py-1 text-right">Bid</th>
            <th className="px-2 py-1 text-right">Ask</th>
            <th className="px-2 py-1 text-center"></th>
            <th className="px-2 py-1 text-right">Bid</th>
            <th className="px-2 py-1 text-right">Ask</th>
            <th className="px-2 py-1 text-right">Last</th>
            <th className="px-2 py-1 text-right">Vol</th>
            <th className="px-2 py-1 text-right">OI</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isAtm = atmStrike != null && row.strike === atmStrike
            return (
              <tr
                key={row.strike}
                className={`border-b border-zinc-900 ${isAtm ? 'bg-zinc-800/60' : ''}`}
              >
                <td className="px-2 py-1 text-right font-mono text-zinc-400">{fmtInt(row.call?.openInterest)}</td>
                <td className="px-2 py-1 text-right font-mono text-zinc-400">{fmtInt(row.call?.volume)}</td>
                <td className="px-2 py-1 text-right font-mono text-zinc-300">{fmtPrice(row.call?.last)}</td>
                <PriceCell
                  contract={row.call}
                  field="bid"
                  side="CALL"
                  armedKey={armedKey}
                  submittingKey={submittingKey}
                  onClick={(action, c) => onCellClick('CALL', action, c)}
                  buyDisabledReason={callBuyDisabledReason}
                />
                <PriceCell
                  contract={row.call}
                  field="ask"
                  side="CALL"
                  armedKey={armedKey}
                  submittingKey={submittingKey}
                  onClick={(action, c) => onCellClick('CALL', action, c)}
                  buyDisabledReason={callBuyDisabledReason}
                />
                <td className="px-2 py-1.5 text-center font-mono font-semibold text-zinc-100">
                  {row.strikeText}
                  {isAtm && <div className="text-[9px] text-zinc-500 leading-none">ATM</div>}
                </td>
                <PriceCell
                  contract={row.put}
                  field="bid"
                  side="PUT"
                  armedKey={armedKey}
                  submittingKey={submittingKey}
                  onClick={(action, c) => onCellClick('PUT', action, c)}
                  buyDisabledReason={putBuyDisabledReason}
                />
                <PriceCell
                  contract={row.put}
                  field="ask"
                  side="PUT"
                  armedKey={armedKey}
                  submittingKey={submittingKey}
                  onClick={(action, c) => onCellClick('PUT', action, c)}
                  buyDisabledReason={putBuyDisabledReason}
                />
                <td className="px-2 py-1 text-right font-mono text-zinc-300">{fmtPrice(row.put?.last)}</td>
                <td className="px-2 py-1 text-right font-mono text-zinc-400">{fmtInt(row.put?.volume)}</td>
                <td className="px-2 py-1 text-right font-mono text-zinc-400">{fmtInt(row.put?.openInterest)}</td>
              </tr>
            )
          })}
        </tbody>
    </Table>
  )
}
